import { HelpArticleBody } from "@/components/registrant/help/help-article-body";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import type { HelpPortalSource } from "@/lib/help-portal";
import {
  type HelpArticle,
  REGISTRANT_HELP_PORTAL,
} from "@/lib/registrant-help";
import type { AnyHelpArticleId } from "@/lib/registrant-help/types";
import { Link } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { useMemo, useState } from "react";

function categoryTitle(
  catalog: HelpPortalSource,
  categoryId: HelpArticle["category"],
): string {
  return (
    catalog.categories.find((category) => category.id === categoryId)?.title ??
    categoryId
  );
}

export function HelpSearchField({
  value,
  onChange,
  autoFocus = false,
}: {
  value: string;
  onChange: (value: string) => void;
  autoFocus?: boolean;
}) {
  return (
    <div className="relative">
      <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
      <Input
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Search help…"
        aria-label="Search help"
        autoFocus={autoFocus}
        className="pl-9"
      />
    </div>
  );
}

export function HelpArticleNav({
  activeId,
  catalog = REGISTRANT_HELP_PORTAL,
}: {
  activeId?: AnyHelpArticleId;
  catalog?: HelpPortalSource;
}) {
  return (
    <nav aria-label="Help topics" className="space-y-4">
      {catalog.categories.map((category) => (
        <div key={category.id}>
          <p className="text-muted-foreground mb-1 text-xs font-semibold tracking-wide uppercase">
            {category.title}
          </p>
          <ul className="space-y-0.5">
            {catalog.articlesInCategory(category.id).map((article) => {
              const isActive = article.id === activeId;
              return (
                <li key={article.id}>
                  <Link
                    to={catalog.articleTo}
                    params={{ articleId: article.id }}
                    className={
                      isActive
                        ? "bg-muted text-foreground block rounded-md px-2 py-1.5 text-sm font-medium"
                        : "text-muted-foreground hover:bg-muted/60 hover:text-foreground block rounded-md px-2 py-1.5 text-sm"
                    }
                  >
                    {article.title}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );
}

export function HelpRelatedArticles({
  article,
  catalog = REGISTRANT_HELP_PORTAL,
}: {
  article: HelpArticle;
  catalog?: HelpPortalSource;
}) {
  if (article.related.length === 0) {
    return null;
  }
  return (
    <section className="border-t pt-6">
      <h2 className="text-foreground mb-3 text-sm font-semibold">See also</h2>
      <ul className="space-y-2">
        {article.related.map((relatedId) => {
          const related = catalog.articles.find(
            (item) => item.id === relatedId,
          );
          if (!related) return null;
          return (
            <li key={relatedId}>
              <Link
                to={catalog.articleTo}
                params={{ articleId: related.id }}
                className="text-primary text-sm font-medium hover:underline"
              >
                {related.title}
              </Link>
              <p className="text-muted-foreground text-sm">{related.summary}</p>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export function HelpSearchResults({
  query,
  catalog = REGISTRANT_HELP_PORTAL,
}: {
  query: string;
  catalog?: HelpPortalSource;
}) {
  const hits = useMemo(() => catalog.searchArticles(query), [catalog, query]);
  if (hits.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        No help topics match “{query}”. {catalog.emptySearchHint}
      </p>
    );
  }
  return (
    <ul className="space-y-3">
      {hits.map(({ article }) => (
        <li key={article.id}>
          <Link
            to={catalog.articleTo}
            params={{ articleId: article.id }}
            className="hover:bg-muted/50 block rounded-lg border p-4"
          >
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <span className="text-foreground font-medium">
                {article.title}
              </span>
              <Badge variant="outline">
                {categoryTitle(catalog, article.category)}
              </Badge>
            </div>
            <p className="text-muted-foreground text-sm">{article.summary}</p>
          </Link>
        </li>
      ))}
    </ul>
  );
}

export function HelpCatalog({
  search,
  onSearchChange,
  catalog = REGISTRANT_HELP_PORTAL,
}: {
  search: string;
  onSearchChange: (value: string) => void;
  catalog?: HelpPortalSource;
}) {
  return (
    <div className="space-y-8">
      <div className="max-w-xl">
        <HelpSearchField value={search} onChange={onSearchChange} autoFocus />
      </div>
      {search.trim() ? (
        <HelpSearchResults query={search} catalog={catalog} />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {catalog.categories.map((category) => (
            <Card key={category.id}>
              <CardHeader>
                <CardTitle>{category.title}</CardTitle>
                <CardDescription>{category.summary}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-1">
                {catalog.articlesInCategory(category.id).map((article) => (
                  <Link
                    key={article.id}
                    to={catalog.articleTo}
                    params={{ articleId: article.id }}
                    className="text-primary block text-sm font-medium hover:underline"
                  >
                    {article.title}
                  </Link>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export function HelpArticlePage({
  article,
  catalog = REGISTRANT_HELP_PORTAL,
}: {
  article: HelpArticle;
  catalog?: HelpPortalSource;
}) {
  const [search, setSearch] = useState("");
  return (
    <div className="grid gap-8 lg:grid-cols-[16rem_minmax(0,1fr)]">
      <aside className="hidden lg:block">
        <HelpArticleNav activeId={article.id} catalog={catalog} />
      </aside>
      <div className="min-w-0 space-y-6">
        <HelpSearchField value={search} onChange={setSearch} />
        {search.trim() ? (
          <HelpSearchResults query={search} catalog={catalog} />
        ) : (
          <>
            <div>
              <Badge variant="outline">
                {categoryTitle(catalog, article.category)}
              </Badge>
              <h1 className="text-foreground mt-2 text-2xl font-bold tracking-tight">
                {article.title}
              </h1>
              <p className="text-muted-foreground mt-1">{article.summary}</p>
            </div>
            <HelpArticleBody article={article} />
            <HelpRelatedArticles article={article} catalog={catalog} />
            <Button variant="outline" asChild>
              <Link to={catalog.indexTo}>All help topics</Link>
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
